import { Card, DatePicker, Form, Select, Skeleton } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import HTTP from "../../services/httpService";
import {
    dateRangePresets,
    disableFutureDates,
    formatDate,
} from "../../services/util";
import { ColumnChart } from "../JiraDashboard/chartUtils";
dayjs.extend(customParseFormat);

const { Option } = Select;
const { RangePicker } = DatePicker;

const chartByOption = [
    { key: "year", title: "Year" },
    { key: "days", title: "Day" },
];

const labelCol = { span: 6 };
const wrapperCol = { span: 18 };

function CommitChart({ projects, users }) {
    const staff_id = localStorage.getItem("x-dashboard-staffid");
    const [yearSelected, setYearSelected] = useState(dayjs());
    const [dateRangeSelected, setDateRangeSelected] = useState([
        dayjs().subtract(7, "day"),
        dayjs(),
    ]);
    const [form] = Form.useForm();
    const [chartData, setChartData] = useState([]);
    const [formData, setFormData] = useState({
        staff_id,
        range_year: yearSelected.get("year"),
        project_id: "-1",
        user_id: "",
        chart_by: chartByOption[0].key,
    });
    const [loader, setLoader] = useState(true);

    const {
        status: commitsStatus,
        data: commitsRes,
        refetch: getCommit,
    } = useQuery(
        "/gitCommit",
        async () =>
            await HTTP("/analytics/getGitCommitsByMonth", { ...formData }),
        { enabled: false },
    );

    useEffect(() => {
        getCommit();
        form.setFieldsValue({
            chart_by: chartByOption[0].key,
            range_year: yearSelected,
        });
    }, []);

    useEffect(() => {
        setLoader(true);
        getCommit();
    }, [formData]);

    useEffect(() => {
        if (commitsStatus === "success") {
            setLoader(false);
            setChartData(commitsRes.data);
        }
    }, [commitsStatus, commitsRes]);

    function handleYearChange(_, dateString) {
        setYearSelected(dateString === "" ? dayjs() : dayjs(dateString));
        setFormData((prev) => ({
            ...prev,
            range_year: dateString || dayjs().get("year"),
        }));
    }

    function handleProjectSelectionChange(e) {
        setFormData((prev) => ({
            ...prev,
            project_id: e ? e.split("#")[0] : -1,
        }));
    }

    function handleUserSelectionChange(user_id) {
        setFormData((prev) => ({ ...prev, user_id }));
    }

    function handleChartBySelection(chart_by) {
        const updated = { ...formData, chart_by };
        if (chart_by === "year") {
            updated.range_year = yearSelected.get("year");
            delete updated.start_date;
            delete updated.end_date;
            form.setFieldsValue({ range_year: yearSelected });
        } else {
            delete updated.range_year;
            const [s, e] = dateRangeSelected;
            updated.start_date = formatDate(s);
            updated.end_date = formatDate(e);
            form.setFieldsValue({ range_date: dateRangeSelected });
        }
        setFormData(updated);
    }

    function handleDateRangeChange(e) {
        const start =
            dayjs(e[0]) < dayjs()
                ? dayjs(e[0]).startOf("day")
                : dayjs().startOf("day");
        const end =
            dayjs(e[1]) < dayjs()
                ? dayjs(e[1]).startOf("day")
                : dayjs().startOf("day");
        setDateRangeSelected([start, end]);
        form.setFieldsValue({ range_date: [start, end] });
        setFormData((prev) => ({
            ...prev,
            start_date: formatDate(start),
            end_date: formatDate(end),
        }));
    }

    function disabledDate(current) {
        return (
            (current && current < dayjs().subtract(90, "day")) ||
            current > dayjs().endOf("day")
        );
    }

    const isReady = !loader && chartData.length > 0;

    return (
        <div
            style={{
                background: "var(--color-bg-card)",
                border: "1px solid var(--color-border)",
                borderRadius: 12,
                padding: "20px 24px",
                marginBottom: 20,
            }}
        >
            {/* Header */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 20,
                }}
            >
                <div
                    style={{
                        width: 3,
                        height: 18,
                        borderRadius: 2,
                        background: "var(--gradient-primary)",
                    }}
                />
                <span
                    style={{
                        color: "var(--color-text)",
                        fontWeight: 600,
                        fontSize: 15,
                    }}
                >
                    Commits
                </span>
            </div>

            {/* Filters */}
            <div
                style={{
                    background: "var(--color-bg-control)",
                    border: "1px solid var(--color-border)",
                    borderRadius: 10,
                    padding: "16px 20px",
                    marginBottom: 20,
                }}
            >
                <Form
                    form={form}
                    labelCol={labelCol}
                    wrapperCol={wrapperCol}
                    size="small"
                >
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "0 24px",
                        }}
                    >
                        <Form.Item
                            label={
                                <span
                                    style={{ color: "var(--color-text-muted)" }}
                                >
                                    Project
                                </span>
                            }
                            name="project_id"
                        >
                            <Select
                                allowClear
                                showSearch
                                placeholder="All Projects"
                                loading={!projects.length}
                                onChange={handleProjectSelectionChange}
                            >
                                {projects.map((e) => (
                                    <Option
                                        key={`${e.project_id}#${e.project_name}`}
                                    >
                                        {e.project_name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item
                            label={
                                <span
                                    style={{ color: "var(--color-text-muted)" }}
                                >
                                    User
                                </span>
                            }
                            name="user_id"
                        >
                            <Select
                                allowClear
                                showSearch
                                mode="multiple"
                                placeholder="All Users"
                                loading={!users.length}
                                onChange={handleUserSelectionChange}
                            >
                                {users.map((e) => (
                                    <Option key={e.committer_email}>
                                        {e.committer_name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item
                            label={
                                <span
                                    style={{ color: "var(--color-text-muted)" }}
                                >
                                    Chart By
                                </span>
                            }
                            name="chart_by"
                        >
                            <Select onChange={handleChartBySelection}>
                                {chartByOption.map((e) => (
                                    <Option key={e.key} value={e.key}>
                                        {e.title}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item
                            shouldUpdate={(prev, cur) =>
                                prev.chart_by !== cur.chart_by
                            }
                            noStyle
                        >
                            {({ getFieldValue }) =>
                                getFieldValue("chart_by") === "year" ? (
                                    <Form.Item
                                        label={
                                            <span
                                                style={{
                                                    color: "var(--color-text-muted)",
                                                }}
                                            >
                                                Year
                                            </span>
                                        }
                                        name="range_year"
                                    >
                                        <DatePicker
                                            onChange={handleYearChange}
                                            picker="year"
                                            allowClear={false}
                                            disabledDate={disableFutureDates}
                                            style={{ width: "100%" }}
                                        />
                                    </Form.Item>
                                ) : (
                                    <Form.Item
                                        label={
                                            <span
                                                style={{
                                                    color: "var(--color-text-muted)",
                                                }}
                                            >
                                                Date Range
                                            </span>
                                        }
                                        name="range_date"
                                    >
                                        <RangePicker
                                            disabledDate={disabledDate}
                                            onChange={handleDateRangeChange}
                                            format="MM/DD/YYYY"
                                            allowClear={false}
                                            style={{ width: "100%" }}
                                            presets={dateRangePresets.filter(
                                                (preset) =>
                                                    preset.label == "Today" ||
                                                    preset.label !==
                                                        "Current Month" ||
                                                    preset.label !==
                                                        "Last Month",
                                            )}
                                        />
                                    </Form.Item>
                                )
                            }
                        </Form.Item>
                    </div>
                </Form>
            </div>

            {/* Chart */}
            {isReady ? (
                <ColumnChart rawData={chartData} height={380} />
            ) : (
                <Skeleton
                    active
                    title={false}
                    paragraph={{ rows: 8, width: "100%" }}
                />
            )}
        </div>
    );
}

export default CommitChart;
